import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { RefreshCw, CheckCircle2, AlertCircle, Zap, ChevronDown, ChevronUp, List } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { restartQuizGeneration } from '@/services/restartQuizGeneration';
import { restartPracticeGeneration } from '@/services/restartPracticeGeneration';

interface IntegrityStats {
  totalModules: number;
  totalContent: number;
  totalQuizzes: number;
  totalLabs: number;
  missingQuizzes: Array<{ course_id: string; module_id: string; module_title: string }>;
  missingLabs: Array<{ course_id: string; module_id: string; module_title: string }>;
}

export const DatabaseIntegrityMonitor = () => {
  const [stats, setStats] = useState<IntegrityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({ quizzes: false, labs: false });
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [showMissingQuizzes, setShowMissingQuizzes] = useState(false);
  const [showMissingLabs, setShowMissingLabs] = useState(false);

  const fetchIntegrityStats = async () => {
    try {
      const { count: moduleCount } = await supabase
        .from('course_modules')
        .select('*', { count: 'exact', head: true })
        .eq('is_enabled', true);

      const { count: contentCount } = await supabase
        .from('module_content')
        .select('*', { count: 'exact', head: true });

      const { count: quizCount } = await supabase
        .from('module_quizzes')
        .select('*', { count: 'exact', head: true });

      const { count: labCount } = await supabase
        .from('generated_practice_labs')
        .select('*', { count: 'exact', head: true });

      const { data: quizModuleIds } = await supabase
        .from('module_quizzes')
        .select('module_id');
      
      const quizIds = quizModuleIds?.map(q => q.module_id) || [];
      
      const { data: modulesWithoutQuizzes } = await supabase
        .from('course_modules')
        .select('course_id, module_id, module_title')
        .eq('is_enabled', true)
        .not('module_id', 'in', `(${quizIds.join(',')})`);

      const { data: labModuleIds } = await supabase
        .from('generated_practice_labs')
        .select('module_id');
      
      const labIds = labModuleIds?.map(l => l.module_id) || [];
      
      const { data: modulesWithoutLabs } = await supabase
        .from('course_modules')
        .select('course_id, module_id, module_title')
        .eq('is_enabled', true)
        .not('module_id', 'in', `(${labIds.join(',')})`);

      setStats({
        totalModules: moduleCount || 0,
        totalContent: contentCount || 0,
        totalQuizzes: quizCount || 0,
        totalLabs: labCount || 0,
        missingQuizzes: modulesWithoutQuizzes || [],
        missingLabs: modulesWithoutLabs || []
      });
    } catch (error) {
      console.error('Failed to fetch integrity stats:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch database integrity stats',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrityStats();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchIntegrityStats, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleGenerateQuizzes = async () => {
    setGenerating(prev => ({ ...prev, quizzes: true }));
    try {
      await restartQuizGeneration();
      toast({
        title: 'Quiz Generation Started',
        description: `Generating quizzes for ${stats?.missingQuizzes.length || 0} modules`,
      });
      setTimeout(fetchIntegrityStats, 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start quiz generation',
        variant: 'destructive'
      });
    } finally {
      setGenerating(prev => ({ ...prev, quizzes: false }));
    }
  };

  const handleGenerateLabs = async () => {
    setGenerating(prev => ({ ...prev, labs: true }));
    try {
      await restartPracticeGeneration();
      toast({
        title: 'Practice Lab Generation Started',
        description: `Generating practice labs for ${stats?.missingLabs.length || 0} modules`,
      });
      setTimeout(fetchIntegrityStats, 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to start practice lab generation',
        variant: 'destructive'
      });
    } finally {
      setGenerating(prev => ({ ...prev, labs: false }));
    }
  };

  const getCompletionPercentage = (completed: number, total: number) => {
    if (total === 0) return 100;
    return Math.round((completed / total) * 100);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading Integrity Stats...
          </CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (!stats) return null;

  const quizPercentage = getCompletionPercentage(stats.totalQuizzes, stats.totalModules);
  const labPercentage = getCompletionPercentage(stats.totalLabs, stats.totalModules);
  const contentPercentage = getCompletionPercentage(stats.totalContent, stats.totalModules);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Content Integrity Monitor</CardTitle>
            <CardDescription>Real-time content completeness tracking</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setAutoRefresh(!autoRefresh)}>
              <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              {autoRefresh ? 'Auto' : 'Manual'}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchIntegrityStats}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <span className="font-medium">Module Content</span>
            </div>
            <Badge variant={contentPercentage === 100 ? "default" : "secondary"}>
              {stats.totalContent} / {stats.totalModules} ({contentPercentage}%)
            </Badge>
          </div>
          <Progress value={contentPercentage} className="h-2" />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {stats.missingQuizzes.length > 0 ? (
                <AlertCircle className="h-5 w-5 text-warning" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-success" />
              )}
              <span className="font-medium">Module Quizzes</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={quizPercentage === 100 ? "default" : "destructive"}>
                {stats.totalQuizzes} / {stats.totalModules} ({quizPercentage}%)
              </Badge>
              {stats.missingQuizzes.length > 0 && (
                <Button size="sm" onClick={handleGenerateQuizzes} disabled={generating.quizzes}>
                  <Zap className="h-4 w-4 mr-1" />
                  {generating.quizzes ? 'Generating...' : `Generate ${stats.missingQuizzes.length}`}
                </Button>
              )}
            </div>
          </div>
          <Progress value={quizPercentage} className="h-2" />
          
          {stats.missingQuizzes.length > 0 && (
            <Collapsible open={showMissingQuizzes} onOpenChange={setShowMissingQuizzes}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <List className="h-4 w-4" />
                    Show {stats.missingQuizzes.length} missing quizzes
                  </span>
                  {showMissingQuizzes ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="space-y-1 max-h-60 overflow-y-auto border rounded-md p-2">
                  {stats.missingQuizzes.map((module) => (
                    <div key={module.module_id} className="flex items-center justify-between p-2 hover:bg-muted rounded text-sm">
                      <div className="flex-1 truncate">
                        <span className="font-medium">{module.module_title}</span>
                        <span className="text-muted-foreground ml-2 text-xs">{module.module_id}</span>
                      </div>
                      <Badge variant="outline" className="ml-2 text-xs">{module.course_id}</Badge>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {stats.missingLabs.length > 0 ? (
                <AlertCircle className="h-5 w-5 text-warning" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-success" />
              )}
              <span className="font-medium">Practice Labs</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={labPercentage === 100 ? "default" : "destructive"}>
                {stats.totalLabs} / {stats.totalModules} ({labPercentage}%)
              </Badge>
              {stats.missingLabs.length > 0 && (
                <Button size="sm" onClick={handleGenerateLabs} disabled={generating.labs}>
                  <Zap className="h-4 w-4 mr-1" />
                  {generating.labs ? 'Generating...' : `Generate ${stats.missingLabs.length}`}
                </Button>
              )}
            </div>
          </div>
          <Progress value={labPercentage} className="h-2" />
          
          {stats.missingLabs.length > 0 && (
            <Collapsible open={showMissingLabs} onOpenChange={setShowMissingLabs}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <List className="h-4 w-4" />
                    Show {stats.missingLabs.length} missing labs
                  </span>
                  {showMissingLabs ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <div className="space-y-1 max-h-60 overflow-y-auto border rounded-md p-2">
                  {stats.missingLabs.map((module) => (
                    <div key={module.module_id} className="flex items-center justify-between p-2 hover:bg-muted rounded text-sm">
                      <div className="flex-1 truncate">
                        <span className="font-medium">{module.module_title}</span>
                        <span className="text-muted-foreground ml-2 text-xs">{module.module_id}</span>
                      </div>
                      <Badge variant="outline" className="ml-2 text-xs">{module.course_id}</Badge>
                    </div>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>

        <div className="pt-4 border-t">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Overall Completion</span>
            <Badge variant={
              quizPercentage === 100 && labPercentage === 100 && contentPercentage === 100
                ? "default" : "secondary"
            }>
              {quizPercentage === 100 && labPercentage === 100 && contentPercentage === 100
                ? 'All Content Complete'
                : `${stats.missingQuizzes.length + stats.missingLabs.length} Items Pending`}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DatabaseIntegrityMonitor;
